import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/api";
import Logo from "@/components/Logo";
import { fmtDate, fmtTime, fmtDateTime, inr, todayISO } from "@/lib/tps";
import { Printer } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ProductionSheet() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (id) {
      api.get(`/orders/${id}`).then((r) => setOrder(r.data));
    } else {
      const t = todayISO();
      api.get(`/orders?start=${t}&end=${t}`).then((r) => setOrders(r.data.filter((o) => !["Cancelled", "Lost"].includes(o.status))));
    }
  }, [id]);

  return (
    <div className="min-h-screen bg-white p-6 md:p-10 max-w-4xl mx-auto" data-testid="print-page">
      <div className="flex items-center justify-between border-b-2 border-[#B86F77] pb-3 mb-5">
        <div className="flex items-center gap-3">
          <Logo size={56} />
          <div>
            <div className="font-heading text-2xl text-[#141E30]">The Palate Stories</div>
            <div className="text-xs text-[#4B5563] tracking-widest uppercase">Patisserie & Bake Studio</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-heading text-lg">Production Sheet</div>
          <div className="text-xs text-[#4B5563]">Printed {fmtDateTime(new Date().toISOString())}</div>
          <Button onClick={() => window.print()} className="mt-2 h-8 no-print bg-[#B86F77] hover:bg-[#A35D65]" data-testid="print-trigger-btn"><Printer className="h-3.5 w-3.5 mr-1" /> Print</Button>
        </div>
      </div>

      {id && order && <SingleOrder order={order} />}
      {!id && (
        <div>
          <div className="font-heading text-xl mb-3">Orders due {fmtDate(todayISO())} · {orders.length}</div>
          {orders.length === 0 && <div className="text-sm text-[#4B5563]">No orders today.</div>}
          {orders.map((o) => <SingleOrder key={o.id} order={o} compact />)}
        </div>
      )}
    </div>
  );
}

function SingleOrder({ order, compact = false }) {
  const it = order.items?.[0] || {};
  return (
    <div className={`mb-6 ${compact ? "border-b border-[#E8E3DF] pb-4" : ""}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#4B5563]">Order</div>
          <div className="font-heading text-xl text-[#B86F77]">{order.order_id}</div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wider text-[#4B5563]">Required</div>
          <div className="font-heading text-xl">{fmtDate(order.required_date)} · {fmtTime(order.required_time)}</div>
          <div className="text-xs text-[#4B5563]">{order.delivery_method}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm border-t border-[#E8E3DF] pt-2">
        <Row l="Customer" v={`${order.customer_name} · ${order.customer_phone}`} />
        <Row l="Theme" v={it.theme} />
        <Row l="Weight" v={it.weight} />
        <Row l="Tier" v={it.tier} />
        <Row l="Flavour" v={it.flavour} />
        <Row l="Message" v={it.cake_message} />
        <Row l="Notes" v={it.customization_notes} />
        <Row l="Delivery Address" v={order.delivery_address} />
        <Row l="Total" v={inr(order.total_price)} />
        <Row l="Balance" v={inr(order.balance_amount)} />
      </div>
      {!compact && order.final_reference_image && (
        <div className="mt-3">
          <div className="text-xs uppercase tracking-wider text-[#4B5563] mb-1">Final Reference Image</div>
          <img src={order.final_reference_image} alt="" className="max-h-72 rounded-md border border-[#E8E3DF]" />
        </div>
      )}
    </div>
  );
}

function Row({ l, v }) {
  return (
    <div className="flex gap-2">
      <span className="text-xs text-[#4B5563] uppercase tracking-wider w-32 shrink-0 pt-0.5">{l}</span>
      <span className="text-sm">{v || "—"}</span>
    </div>
  );
}
